<docs>
Diese Komponente stellt den Inhalt eines Modals dar und bietet eine standardisierte Kopfzeile mit Titel,
Schließen- und Fertig-Buttons. Sie unterstützt optionale Such- und Aktionsbereiche.

Auf mobilen Geräten ändert sich das Verhalten des Action-Footers, wenn die Tastatur geöffnet ist:
Der Button wird zu einem Icon-only Floating Action Button (FAB) verkleinert und mit einer Bounce-Animation
in der rechten unteren Ecke angezeigt.
</docs>

<script setup lang="ts">
import type { JSX } from '@ionic/core'
import { closeCircleOutline } from 'ionicons/icons'

useAccessibleScrolling()

defineProps<JSX.IonModal & {
	title: string
	showBackButton?: boolean
}>()

const emit = defineEmits(['close'])

const { isOpen } = useKeyboard()

const onClose = async () => {
	emit('close')
	await modalController.dismiss()
}
</script>

<template>
	<ion-header
		v-bind="$attrs"
		role="none"
	>
		<ion-toolbar>
			<ion-buttons
				v-if="showBackButton"
				slot="start"
			>
				<ion-back-button
					text="Zurück"
					@click.stop
				/>
			</ion-buttons>
			<ion-title>{{ title }}</ion-title>
			<ion-buttons
				slot="end"
			>
				<ion-button
					data-cy="modal-close"
					aria-label="Schließen"
					@click="onClose"
				>
					<ion-icon
						slot="icon-only"
						:icon="closeCircleOutline"
						aria-hidden="true"
					/>
				</ion-button>
			</ion-buttons>
		</ion-toolbar>
	</ion-header>
	<ion-content
		role="region"
		:aria-label="`${title} - Inhalt`"
		:force-overscroll="false"
	>
		<search v-if="$slots['search']">
			<slot name="search" />
		</search>
		<slot />
		<div
			v-if="$slots.actions"
			class="spacer"
		/>
		<div
			v-if="$slots.actions"
			class="floating-action-container"
			:class="{ 'keyboard-visible': isOpen }"
		>
			<slot name="actions" />
		</div>
	</ion-content>
</template>

<style lang="scss" scoped>
.floating-action-container {
	display: flex;
	justify-content: center;
	align-items: center;
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	padding: 16px;
	background-image: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255, 1) 90%);
	z-index: 100;
	box-shadow: none;
	transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;

	&:deep(ion-button) {
		flex-grow: 1;
		width: 100%;
		height: auto;
		transition: all 0.3s cubic-bezier(0.34, 1.2, 0.64, 1);
	}

	&.keyboard-visible {
		justify-content: flex-end;
		background-image: none;
		padding: 16px;

		&:deep(ion-button) {
			flex-grow: 0;
			width: 56px;
			height: 56px;
			--border-radius: 100px;
			--padding-start: 0;
			--padding-end: 0;

			// Hide text content
			font-size: 0;

			i {
				margin: 0;
			}
		}
	}
}

search {
	padding: 20px 15px;
}

ion-back-button.in-toolbar::part(native) {
	color: var(--ion-color-secondary);
}

ion-button.in-toolbar::part(native) {
	color: var(--ion-color-secondary);
}

ion-toolbar {
	--background: #FFFFFF;
	--border-width: 0 !important;
	--padding-top: 5px;
	--padding-bottom: 10px;
}

ion-searchbar {
	padding: 0;
	--background: #FFFFFF;
	border: 1px solid var(--ion-color-secondary);
	border-radius: 8px;

	&.searchbar-has-focus {
		border: 2px solid var(--ion-color-secondary);
	}

	:deep(.searchbar-input) {
		border-radius: 8px
	}
}

ion-title {
	font-size: 20px;
	font-weight: 700;
}

.header-md {
	box-shadow: none;
	ion-title {
		font-weight: 400;
	}
}

.spacer {
	height: 80px;
}
</style>
